import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteDocsPage, fetchDocsPages } from "@/api/docsSiteApi";
import { formatPageUpdatedAt, publicPageUrl } from "../docsSiteConstants";
import DocsPageEditor from "./DocsPageEditor";
import { SectionCard, StatusBadge } from "./docsSiteUi";

function PageRow({ page, settings, published, onEdit, onDelete }) {
  const pageUrl = publicPageUrl(settings, page.slug);
  const isPublished = page.status === "published";

  return (
    <article className="group flex flex-col gap-4 rounded-xl border border-slate-200/80 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-sm sm:flex-row sm:items-center dark:border-slate-700/80 dark:bg-slate-900/40 dark:hover:border-slate-600">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ${
            isPublished
              ? "bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/50"
              : "bg-slate-100 text-slate-500 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700"
          }`}
        >
          <i className={isPublished ? "ri-file-text-fill" : "ri-draft-line"} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">{page.title}</h3>
            <StatusBadge status={page.status} size="compact" />
          </div>
          <p className="mt-1 truncate font-mono text-xs text-slate-500">/{page.slug}</p>
          <p className="mt-1 text-xs text-slate-400">Updated {formatPageUpdatedAt(page.updatedAt)}</p>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
        <button type="button" className="ui-btn-secondary !px-3 !py-2 text-xs" onClick={() => onEdit(page.id)}>
          <i className="ri-pencil-line" aria-hidden />
          Edit
        </button>
        <button type="button" className="ui-btn-secondary !px-3 !py-2 text-xs" onClick={() => onDelete(page.id)}>
          Delete
        </button>
        {isPublished && (
          <a
            href={pageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`ui-btn-primary !px-3 !py-2 text-xs ${!published ? "pointer-events-none opacity-50" : ""}`}
            aria-disabled={!published}
          >
            <i className="ri-external-link-line" aria-hidden />
            View live
          </a>
        )}
      </div>
    </article>
  );
}

export default function DocsPagesPanel({ settings, published, onPagesChange }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [editorPageId, setEditorPageId] = useState(null);
  const [showNewEditor, setShowNewEditor] = useState(false);

  const loadPages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDocsPages();
      setPages(Array.isArray(data) ? data : []);
      onPagesChange?.(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Failed to load pages");
    } finally {
      setLoading(false);
    }
  }, [onPagesChange]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const filteredPages = useMemo(() => {
    return pages.filter((page) => {
      const matchesSearch =
        page.title.toLowerCase().includes(search.toLowerCase()) ||
        page.slug.toLowerCase().includes(search.toLowerCase());
      const matchesFilter = filter === "all" || page.status === filter;
      return matchesSearch && matchesFilter;
    });
  }, [pages, search, filter]);

  const counts = useMemo(
    () => ({
      all: pages.length,
      published: pages.filter((p) => p.status === "published").length,
      draft: pages.filter((p) => p.status === "draft").length,
    }),
    [pages],
  );

  async function handleDelete(pageId) {
    if (!window.confirm("Delete this page?")) return;
    try {
      await deleteDocsPage(pageId);
      await loadPages();
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  }

  return (
    <div className="space-y-6">
      <SectionCard
        icon="ri-file-text-line"
        title="Documentation pages"
        description="Create HTML pages or upload Word documents. Only published pages appear on your public site."
        action={
          <button type="button" className="ui-btn-primary" onClick={() => setShowNewEditor(true)}>
            <i className="ri-add-line" aria-hidden />
            New page
          </button>
        }
      >
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            type="search"
            placeholder="Search pages…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ui-field flex-1"
          />
          <select className="ui-field sm:w-40" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All ({counts.all})</option>
            <option value="published">Published ({counts.published})</option>
            <option value="draft">Draft ({counts.draft})</option>
          </select>
        </div>

        {error && <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {loading ? (
          <p className="text-sm text-slate-500">Loading pages…</p>
        ) : filteredPages.length === 0 ? (
          <p className="text-sm text-slate-500">No pages yet. Create your first documentation page.</p>
        ) : (
          <div className="space-y-3">
            {filteredPages.map((page) => (
              <PageRow
                key={page.id}
                page={page}
                settings={settings}
                published={published}
                onEdit={setEditorPageId}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {(showNewEditor || editorPageId) && (
        <DocsPageEditor
          pageId={editorPageId}
          onClose={() => {
            setShowNewEditor(false);
            setEditorPageId(null);
          }}
          onSaved={loadPages}
        />
      )}
    </div>
  );
}
